import { useState, useEffect, useRef, useCallback } from 'react';
import { analytics } from 'lib/analytics/analytics';
import type { LocationMatchMethod } from 'utils/locationMatch';
import {
  ANALYTICS_EVENTS,
  DEFAULT_PRODUCT,
  GTM_EVENTS,
  storageKeysForProduct,
  type ClaimPopupVariant,
  type ClaimSizingKitProduct,
} from './constants';

const pushToDataLayer = (
  event: string,
  data?: Record<string, unknown>,
): void => {
  if (typeof window === 'undefined') return;
  try {
    (
      window as Window & { dataLayer?: Record<string, unknown>[] }
    ).dataLayer?.push({ event, ...data });
  } catch {
    // ignore
  }
};

const getDeviceType = (): string => {
  if (typeof window === 'undefined') return 'unknown';
  const width = window.innerWidth;
  if (width < 768) return 'mobile';
  if (width < 1024) return 'tablet';
  return 'desktop';
};

type UseIdlePopupOptions = {
  delayMs?: number;
  region?: string;
  product?: ClaimSizingKitProduct;
  variant?: ClaimPopupVariant;
  matchMethod?: LocationMatchMethod;
  enabled?: boolean;
};

/**
 * Shows a popup once the user has been on the page for `delayMs`. Dismissal and
 * claim state persist in localStorage, so it never re-appears across sessions.
 */
export function useIdlePopup({
  delayMs = 60_000,
  region,
  product = DEFAULT_PRODUCT,
  variant = 'sizing_kit',
  matchMethod = 'none',
  enabled = true,
}: UseIdlePopupOptions = {}) {
  const [isVisible, setIsVisible] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const hasTrackedMount = useRef(false);

  useEffect(() => {
    if (!enabled) {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
      return;
    }

    const storageKeys = storageKeysForProduct(product);
    const baseData = {
      delayMs,
      region,
      product,
      variant,
      matchMethod,
      page: window?.location?.pathname,
      device: getDeviceType(),
    };

    try {
      const wasClaimed = localStorage.getItem(storageKeys.claimed);
      const wasDismissed = localStorage.getItem(storageKeys.dismissed);

      if (wasClaimed || wasDismissed) {
        if (!hasTrackedMount.current) {
          hasTrackedMount.current = true;
          const reason = wasClaimed ? 'claimed' : 'dismissed';
          analytics.track(ANALYTICS_EVENTS.SUPPRESSED, { ...baseData, reason });
          pushToDataLayer(GTM_EVENTS.SUPPRESSED, { ...baseData, reason });
        }
        return;
      }
    } catch {
      // localStorage unavailable (privacy mode) — proceed
    }

    // Fire tracking events only once (guard against Strict Mode double-mount)
    if (!hasTrackedMount.current) {
      hasTrackedMount.current = true;
      analytics.track(ANALYTICS_EVENTS.TIMER_STARTED, baseData);
      pushToDataLayer(GTM_EVENTS.TIMER_STARTED, baseData);
    }

    timerRef.current = setTimeout(() => {
      setIsVisible(true);
      analytics.track(ANALYTICS_EVENTS.POPUP_SHOWN, baseData);
      pushToDataLayer(GTM_EVENTS.POPUP_SHOWN, baseData);
    }, delayMs);

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [delayMs, region, product, variant, matchMethod, enabled]);

  const dismiss = useCallback(() => {
    setIsVisible(false);
    const data = {
      region,
      product,
      variant,
      matchMethod,
      page: window?.location?.pathname,
      device: getDeviceType(),
    };
    analytics.track(ANALYTICS_EVENTS.POPUP_DISMISSED, data);
    pushToDataLayer(GTM_EVENTS.POPUP_DISMISSED, data);
    try {
      const storageKeys = storageKeysForProduct(product);
      localStorage.setItem(storageKeys.dismissed, 'true');
    } catch {
      // ignore
    }
  }, [region, product, variant, matchMethod]);

  const markClaimed = useCallback(() => {
    setIsVisible(false);
    const data = {
      region,
      product,
      variant,
      matchMethod,
      page: window?.location?.pathname,
      device: getDeviceType(),
    };
    analytics.track(ANALYTICS_EVENTS.CLAIMED, data);
    pushToDataLayer(GTM_EVENTS.CLAIMED, data);
    try {
      const storageKeys = storageKeysForProduct(product);
      localStorage.setItem(storageKeys.claimed, 'true');
      localStorage.setItem(storageKeys.dismissed, 'true');
    } catch {
      // ignore
    }
  }, [region, product, variant, matchMethod]);

  const cancel = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    setIsVisible(false);
    const data = {
      region,
      product,
      variant,
      matchMethod,
      page: window?.location?.pathname,
      device: getDeviceType(),
    };
    analytics.track(ANALYTICS_EVENTS.CANCELLED, data);
    pushToDataLayer(GTM_EVENTS.CANCELLED, data);
  }, [region, product, variant, matchMethod]);

  return { isVisible, cancel, dismiss, markClaimed };
}
